import { useState, useMemo, useCallback } from 'react';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useAssistantStore } from '@/stores/use-assistant-store';

export interface ContextDoc {
  id: string;
  title: string;
  /** Auto-injected from the active entity — not user-pinned */
  isActive?: boolean;
}

export interface UseAssistantContextReturn {
  /** Pinned documents passed to the agent on send */
  contextDocs: ContextDoc[];
  /** All workspace docs not yet pinned, filtered by search */
  filteredAvailable: ContextDoc[];
  /** Current search query for the doc picker */
  docSearch: string;
  setDocSearch: (q: string) => void;
  addContext: (id: string) => void;
  removeContext: (id: string) => void;
  clearContext: () => void;
}

export function useAssistantContext(activeEntityId?: string | null): UseAssistantContextReturn {
  const allDocs = useWorkspaceDocs();
  const allDocuments = useMemo(() => allDocs.filter((d) => !d.isArchived), [allDocs]);

  const contextDocIds = useAssistantStore((s) => s.contextDocIds);
  const setContextDocIds = useAssistantStore((s) => s.setContextDocIds);

  const [docSearch, setDocSearch] = useState('');

  const contextDocs = useMemo(() => {
    const pinned = allDocuments
      .filter((d) => contextDocIds.includes(d.id))
      .map((d) => ({ id: d.id, title: d.title || d.id }));

    // Auto-prepend the active entity if it's not already pinned
    if (activeEntityId && !contextDocIds.includes(activeEntityId)) {
      const activeDoc = allDocuments.find((d) => d.id === activeEntityId);
      if (activeDoc) {
        return [{ id: activeDoc.id, title: activeDoc.title || activeDoc.id, isActive: true }, ...pinned];
      }
    }

    return pinned;
  }, [allDocuments, contextDocIds, activeEntityId]);

  const filteredAvailable = useMemo(() => {
    const excludeIds = new Set([...contextDocIds, ...(activeEntityId ? [activeEntityId] : [])]);
    const available = allDocuments
      .filter((d) => !excludeIds.has(d.id))
      .map((d) => ({ id: d.id, title: d.title || d.id }));
    return docSearch.trim()
      ? available.filter((d) => d.title.toLowerCase().includes(docSearch.toLowerCase()))
      : available;
  }, [allDocuments, contextDocIds, activeEntityId, docSearch]);

  const addContext = useCallback((id: string) => {
    setContextDocIds([...contextDocIds, id]);
  }, [contextDocIds, setContextDocIds]);

  const removeContext = useCallback((id: string) => {
    setContextDocIds(contextDocIds.filter((d) => d !== id));
  }, [contextDocIds, setContextDocIds]);

  const clearContext = useCallback(() => {
    setContextDocIds([]);
  }, [setContextDocIds]);

  return {
    contextDocs,
    filteredAvailable,
    docSearch,
    setDocSearch,
    addContext,
    removeContext,
    clearContext,
  };
}
